import { useCallback } from 'react'

import { Screen } from 'constants/tracking/screens'
import { UiState } from '@marketplace-web/shared/ui-state-util'
// Temporary workaround. Resolve ASAP. Do not add more ignores like this.
// eslint-disable-next-line eslint-comments/no-restricted-disable
// eslint-disable-next-line @nx/enforce-module-boundaries
import { transformParcelDtoToModel } from '@marketplace-web/shipping/package-size-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import { ItemStatus } from '../constants'
import { getItemEdit } from '../api'
import { itemUploadStartEventExtra } from '../transformers/events'
import { transformItemEditDto } from '../transformers/item-edit'
import { useAttributesContext } from '../contexts/AttributesContext'
import { useDynamicAttributesContext } from '../contexts/DynamicAttributesContext'
import { useUiStateContext } from '../contexts/UiStateContext'

type Props = {
  onCurrencyUpdate: (currency: string) => void
}

const useFetchItem = ({ onCurrencyUpdate }: Props) => {
  const { track } = useTracking()

  const {
    setUiState,
    itemStatus: [, setItemStatus],
    canBumpItem: [, setCanBumpItem],
  } = useUiStateContext()
  const { initializeAttributes } = useAttributesContext()
  const { fetchCategoryAttributes, setSelectedDynamicAttributeValues } =
    useDynamicAttributesContext()

  const fetchItem = useCallback(
    async ({ id, tempUuid }: { id: number | null; tempUuid: string }) => {
      if (!id) {
        setUiState(UiState.Success)
        track(itemUploadStartEventExtra({ tempUuid, screenName: Screen.ItemUpload }))

        return
      }

      const response = await getItemEdit({ id })

      if ('errors' in response) {
        setUiState(UiState.Failure)

        return
      }

      const item = transformItemEditDto(response.item)
      const parcel = response.parcel && transformParcelDtoToModel(response.parcel)

      setCanBumpItem(item.canPushUp)
      initializeAttributes(item, parcel)

      const itemStatus = item.isDraft ? ItemStatus.DraftEdit : ItemStatus.Edit
      setItemStatus(itemStatus)
      onCurrencyUpdate(item.currency)

      fetchCategoryAttributes(item.catalogId, itemStatus)
      setSelectedDynamicAttributeValues(item.itemAttributes)
      setUiState(UiState.Success)
      track(
        itemUploadStartEventExtra({
          tempUuid,
          screenName: item.isDraft ? Screen.ItemDraft : Screen.ItemEdit,
        }),
      )
    },
    [
      track,
      setUiState,
      setItemStatus,
      initializeAttributes,
      fetchCategoryAttributes,
      setSelectedDynamicAttributeValues,
      setCanBumpItem,
      onCurrencyUpdate,
    ],
  )

  return { fetchItem }
}

export default useFetchItem
